/**
 * Which FR-008 certificate blocks a member can wait out, and which need a human.
 *
 * The server refuses a certificate with a `PRECONDITION_FAILED` whose message is
 * the `CertificateBlockReason` (`apps/api/src/modules/membership-certificate/
 * certificate-content.ts`). Every one of them used to surface the same
 * "try again later" copy — correct for an upstream outage, actively misleading
 * for a member whose record is missing a membership start date, where retrying
 * can never succeed and only a member-services correction will.
 *
 * Owner decision 2026-09-24: distinguish the two and point the second group at
 * member services.
 *
 * TRANSIENT — the upstream read failed; the data is presumably fine:
 *   - `profile-unavailable`   the member-profile read was unavailable
 *   - `billing-unavailable`   the invoice read was unavailable
 *
 * PERMANENT (until a person acts) — the read SUCCEEDED and the data it returned
 * cannot produce a certificate:
 *   - `membership-start-date-missing`  no start date on the member record
 *   - `profile-incomplete`            blank name/member number, or a start date
 *                                     that is not a real calendar date
 *   - `fee-status-unavailable`        an invoice carries a status we cannot
 *                                     classify as paid or unpaid
 *
 * `fee-status-unavailable` is the debatable one, and it is deliberately treated
 * as permanent: it means the invoice was READ successfully and its status was
 * unrecognised, which is an upstream data problem a retry will reproduce
 * exactly. Grouping it with the outages would leave the member refreshing a page
 * that cannot change.
 *
 * Unknown/absent messages fall back to TRANSIENT — the conservative arm. A
 * composition error or a network failure is not the member's record being
 * broken, and wrongly telling someone to contact member services is worse than
 * wrongly telling them to try again.
 */

/** Block reasons that a later retry cannot resolve — only a person can. */
const PERMANENT_BLOCK_REASONS: ReadonlySet<string> = new Set([
  'membership-start-date-missing',
  'profile-incomplete',
  'fee-status-unavailable',
]);

/**
 * True when the failure behind `error` is one only member services can clear.
 *
 * Reads the thrown value's `message` — a tRPC client error carries the server's
 * message verbatim. Anything unrecognised is reported as NOT permanent.
 */
export function isPermanentCertificateBlock(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : typeof error === 'object' && error !== null && 'message' in error
          ? String((error as { message: unknown }).message)
          : '';
  return PERMANENT_BLOCK_REASONS.has(message.trim());
}
